import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { User } from '@supabase/supabase-js';
import { isGoogleSignInConfigured, isSupabaseAuthConfigured, supabase } from '../utils/supabase';

type AuthStatus = 'loading' | 'signed-out' | 'signed-in' | 'unconfigured';

interface AuthContextValue {
  clearError: () => void;
  error: string | null;
  isGoogleConfigured: boolean;
  isConfigured: boolean;
  signInWithGoogleToken: (credential: string) => Promise<void>;
  signOut: () => Promise<void>;
  status: AuthStatus;
  user: User | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>(isSupabaseAuthConfigured ? 'loading' : 'unconfigured');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!supabase) {
      return undefined;
    }

    let isActive = true;

    supabase.auth.getSession().then(({ data, error: sessionError }) => {
      if (!isActive) {
        return;
      }

      if (sessionError) {
        setError(sessionError.message);
      }

      const sessionUser = data.session?.user ?? null;
      setUser(sessionUser);
      setStatus(sessionUser ? 'signed-in' : 'signed-out');
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      const sessionUser = session?.user ?? null;
      setUser(sessionUser);
      setStatus(sessionUser ? 'signed-in' : 'signed-out');
      setError(null);
    });

    return () => {
      isActive = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const signInWithGoogleToken = useCallback(async (credential: string) => {
    if (!supabase) {
      setError('Supabase Auth is not configured.');
      return;
    }

    if (!credential) {
      setError('Google did not return a credential.');
      return;
    }

    setError(null);
    setStatus('loading');
    const { error: signInError } = await supabase.auth.signInWithIdToken({
      provider: 'google',
      token: credential,
    });

    if (signInError) {
      setError(signInError.message);
      setStatus('signed-out');
    }
  }, []);

  const signOut = useCallback(async () => {
    if (!supabase) {
      setError('Supabase Auth is not configured.');
      return;
    }

    setError(null);
    const { error: signOutError } = await supabase.auth.signOut();
    if (signOutError) {
      setError(signOutError.message);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo<AuthContextValue>(() => ({
    clearError,
    error,
    isGoogleConfigured: isGoogleSignInConfigured,
    isConfigured: isSupabaseAuthConfigured,
    signInWithGoogleToken,
    signOut,
    status,
    user,
  }), [clearError, error, signInWithGoogleToken, signOut, status, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return value;
}
